import { ServiceStoreConfig } from "./types/ServiceStoreTypes";

// Import categorized service stores
import { crmStores } from "./serviceStores/crm.stores";
import { departmentStores } from "./serviceStores/department.stores";
import { financialStores } from "./serviceStores/financial.stores";
import { inventoryStores } from "./serviceStores/inventory.stores";
import { manufacturingStores } from "./serviceStores/manufacturing.stores";
import { organizationDataStores } from "./serviceStores/organization-data.stores";
import { payrollStores } from "./serviceStores/payroll.stores";
import { qualityControlStores } from "./serviceStores/qualityControl.stores";
import { dashboardsStores } from "./serviceStores/dashboards.stores";
import { schedulingStores } from "./serviceStores/scheduling.stores";
import { yardManagementStores } from "./serviceStores/yardManagement.stores";
import { ntxYardStores } from "./serviceStores/ntxYard.stores";
import { systemStores } from "./serviceStores/system.stores";
import { userStores } from "./serviceStores/user.stores";

/**
 * Main Service Store Configuration
 * Combines all categorized service stores into a single array
 *
 * Categories are organized based on the api-types directory structure:
 * - CRM: customers, components, vendors
 * - Department: departments
 * - Financial: quotes, quote-lines, tax-rates, wa-tax-lookup
 * - Inventory: the @intrasync/inventory-management module's `inventory-*`
 *   service stores + its host-owned services (warehouses, shipping-carriers)
 * - Manufacturing: products, bom-components, forms, labor-types, product-bom, product-labor, manhole-*
 * - Organization Data: projects, project-phases, project-phase-items, project-activity, project-documents, project-financials, project-milestones
 * - Scheduling: form-capacity, product-form-mappings, production-schedules, sales-orders, sales-order-lines
 * - System: api-services, notifications
 * - User: users
 * - Payroll: everee-embedded-sessions, everee-workers, everee-worker-data, etc.
 */
/**
 * Services that are NOT usable on the NTX backend and would fire a failed
 * initial `find()` (→ a user-facing error toast) on every load. We suppress
 * their startup probe via `disableInitialFind` (26.7.13). Grouped by reason:
 *
 * - Designer file/markup services (from @intrasync/stack-designer) — no designer
 *   file backend on NTX: drawings, revisions, sheets, markup-config,
 *   manhole-submittal-packages.
 * - Payroll services not built on this backend (NotFound).
 * - Custom-method-only everee services (MethodNotAllowed — no `find`).
 * - Everee services awaiting a company-ID config (BadRequest) — remove from
 *   this set once the Everee company ID is provisioned (DevOps).
 *
 * The stores still exist and support on-demand `find()`; we only skip the
 * eager connect-time probe. Remove a name here the moment its service is
 * actually served by the NTX API.
 */
const NTX_UNAVAILABLE_SERVICES = new Set<string>([
  // designer file/markup (no backend on NTX)
  "drawings",
  "revisions",
  "sheets",
  "markup-config",
  "manhole-submittal-packages",
  // payroll — not built on NTX (NotFound)
  "pay-periods",
  "payroll-entries",
  "vending-transactions",
  "everee-candidates",
  "payroll-gl-mappings",
  "payroll-gl-overrides",
  "tcp-hours",
  "tcp-employees",
  "timesheet-import",
  "timesheet-csv-import",
  // everee — custom-method-only (MethodNotAllowed, no find)
  "everee-workers",
  "everee-early-pay",
  "everee-realtime-payroll",
  "everee-embedded-sessions",
  "everee-embedded-workers",
  "everee-gross-earnings",
  "everee-paystub-bulk",
  // everee — awaiting company-ID config (BadRequest)
  "everee-worker-data",
  "everee-files",
  "everee-timesheets",
  "everee-payables",
  "everee-payment-history",
  "everee-expenses",
  "everee-work-locations",
  // inventory-scan — the module's scan endpoint requires a `code` query param
  // (BadRequest on a bare find), so the connect-time probe can never succeed.
  // The Scan tab's on-demand calls always pass a code and work fine.
  "inventory-scan",
]);

const rawServiceStores: ServiceStoreConfig[] = [
  ...crmStores,
  ...departmentStores,
  ...financialStores,
  ...inventoryStores,
  ...manufacturingStores,
  ...organizationDataStores,
  ...payrollStores,
  ...qualityControlStores,
  ...dashboardsStores,
  ...schedulingStores,
  ...yardManagementStores,
  ...ntxYardStores,
  ...systemStores,
  ...userStores,
];

// Suppress the startup find() probe for services this backend doesn't serve,
// so they never raise a NotFound/MethodNotAllowed/BadRequest error toast on load.
const serviceStores: ServiceStoreConfig[] = rawServiceStores.map((config) =>
  NTX_UNAVAILABLE_SERVICES.has(config.name)
    ? { ...config, options: { ...config.options, disableInitialFind: true } }
    : config
);

export default serviceStores;
